export const en = {
  common: {
    loading: 'Loading...',
    error: 'Error',
    back: 'Back',
    logout: 'Log out',
    language: 'עברית',
    save: 'Save',
    cancel: 'Cancel',
    toggle_language: 'Toggle language',
  },
  onboarding: {
    intro_headline: 'One system for the whole business',
    intro_subline: 'Calendar, clients, services and your page. All in one place',
    intro_cta: 'Let’s begin',
    signin_title: 'First, who you are',
    signin_subtitle: "We'll email you a code, then get straight to your business.",
    or: 'or',
    rail_label: 'Setup chapters',
    rail_title: 'Setting up',
    rail_subtitle: 'Fill these in any order. Everything saves as you go.',
    chapter_identity: 'Business details',
    chapter_offering: 'Services',
    chapter_team: 'Team',
    chapter_hours: 'Opening hours',
    chapter_storefront: 'Business profile',
    chapter_pending:
      'This chapter is not built yet. The shell, the marks and the draft already work.',
    identity_name_label: 'Business name',
    identity_name_placeholder: 'e.g. Rene Salon',
    identity_slug_label: 'Your page address',
    identity_slug_free: 'That address is free',
    identity_slug_taken: 'That address is taken — pick another',
    identity_address_label: 'Business address',
    identity_address_placeholder: 'Start typing, then pick from the list',
    identity_address_unpinned: 'Pick an address from the list so we can place you on the map',
    identity_trade_label: 'Main trade',
    identity_multi_trade: 'This business offers more than one trade',
    prompt_identity: 'Name, address and trade',
    prompt_offering: 'What you offer',
    prompt_team: 'Who works with you',
    prompt_hours: 'When you can be booked',
    prompt_storefront: 'Photo, phone and policy',
    summary_services: '{{count}} services',
    summary_staff: '{{count}} team members',
    summary_hours: 'Full week for {{count}}',
    summary_photo: 'Photo',
    mark_todo: 'Not started',
    mark_warn: 'Started, still incomplete',
    mark_done: 'Done',
    handoff: 'Continue on your phone \u2190',
  },
  login: {
    intro_headline: 'One system to run the whole business',
    intro_subline: 'Calendar, clients, services and your page. All in one place.',
    intro_cta: 'Sign in to your business',
    intro_new_business: "Don't have a business on Slikerr yet? Create one",
    back_to_email: 'Use a different address',
    code_title: 'The code we sent you',
    title: 'Sign in',
    email_label: 'Email address',
    email_placeholder: 'name@example.com',
    send_code: 'Send code',
    sign_in: 'Sign in',
    code_label: 'Verification code',
    code_placeholder: '6 digits',
    verify: 'Verify',
    sending: 'Sending...',
    verifying: 'Verifying...',
    email_code_sent_notice:
      'If you have an account with this address, a verification code is on its way. The code is valid for 10 minutes.',
    create_business_title: "Let's set up your business",
    create_business_subtitle: 'Almost there. You can change any of this later.',
    create_business_name_label: 'Business name',
    create_business_name_placeholder: 'e.g. Brow Bar TLV',
    create_business_slug_label: 'Your page address',
    create_business_slug_hint: 'We picked a random one - feel free to change it',
    create_business_submit: 'Create business',
    create_business_creating: 'Creating...',
    select_business: 'Select business',
    error_invalid_email: 'Invalid email address',
    error_invalid_code: 'Invalid code',
    error_wrong_code: 'Invalid or expired code, please try again',
    error_rate_limited: 'Too many attempts, please try again later',
    error_generic: 'Something went wrong, please try again',
    apple_button: 'Continue with Apple',
    email_hint: 'We will email you a code. No existing account needed.',
    apple_error: 'Apple sign-in failed, please try again',
    apple_admin_blocked:
      "Apple sign-in isn't available for admin accounts. Use email or Google instead.",
    apple_private_relay:
      'We need your real email address to link this to your business. Turn off "Hide My Email" for Slikerr in your Apple ID settings, or sign in with Google or an email code.',
    use_oauth_instead:
      'This address is already registered through Google or Apple. Sign in with that button.',
    iap_signin_failed:
      "Automatic admin sign-in didn't work for this Google account. Sign in with your admin email below.",
    error_business_name_required: 'Please enter a business name',
    error_slug_taken: 'That address is already taken. Please try another.',
  },
  phoneGate: {
    title: 'Add a verified phone number',
    subtitle:
      "Clients call you directly on this number, and you'll need it to book at another business too. This is required before you can enter the dashboard.",
    phone_label: 'Phone number',
    phone_placeholder: '05X-XXXXXXX',
    send_code: 'Send code',
    sending: 'Sending...',
    code_label: 'Verification code (6 digits)',
    verify: 'Verify',
    verifying: 'Verifying...',
    change_number: 'Change number',
    wrong_code: 'Invalid or expired code, please try again',
    rate_limited: 'Too many attempts, please try again later',
    already_linked: 'This number is already verified on another account, try a different one',
    generic_error: 'Something went wrong, please try again',
  },
  nav: {
    services: 'Services',
    staff: 'Staff & Stations',
    calendar: 'Calendar',
    clients: 'Clients',
    settings: 'Settings',
    growth: 'Growth',
    reminders: 'Reminders',
    reviews: 'Reviews',
    analytics: 'Analytics',
    ai: 'AI Agent',
    stealth_mode: 'Stealth mode',
  },
  dashboard: {
    title: 'Dashboard',
    welcome: 'Welcome',
  },
  settings: {
    title: 'Business Settings',
    name_label: 'Business name',
    instagram_label: 'Instagram link',
    instagram_placeholder: '@handle or https://instagram.com/…',
    save: 'Save changes',
    saving: 'Saving...',
    success: 'Profile updated successfully',
    read_only_notice: 'Read-only - no edit permission',
    name_required: 'Business name is required',
    // T25 — publish gate
    visibility_label: 'Business visibility',
    published_status: 'Published - booking page is live',
    unpublished_status: 'Hidden - booking page returns 404',
    pending_review_status: 'Your business is waiting for our team to approve it',
    pending_review_body:
      'You can keep setting up services, staff and working hours. We will let you know as soon as your page is approved for publishing.',
    publish_button: 'Publish',
    unpublish_button: 'Unpublish',
    publish_saving: 'Saving...',
    publish_success: 'Visibility updated successfully',
    // T30 — overlap-confirm opt-in
    overlap_label: 'Double-booking',
    overlap_description:
      'When on, rescheduling or creating an appointment that overlaps another one for the same staff member asks to confirm instead of blocking it outright. Working hours, breaks, and time off are never affected.',
    overlap_enabled_status: 'On - overlaps can be confirmed',
    overlap_disabled_status: 'Off - overlaps are always blocked',
    overlap_enable_button: 'Turn on',
    overlap_disable_button: 'Turn off',
    overlap_saving: 'Saving...',
    overlap_success: 'Setting updated successfully',
    // Shareable public booking link
    share_link_label: 'Your booking page link',
    share_link_copy_button: 'Copy link',
    share_link_copied: 'Copied!',
    share_link_view_live: 'View live',
    share_link_preview: 'Preview',
    share_link_unpublished_notice:
      "This link won't work until you publish your business - see the toggle above.",
    // #95 - the marketplace profile fields customers actually see
    marketplace_heading: 'What customers see',
    phone_label: 'Business phone',
    phone_placeholder: '050-1234567',
    phone_too_long: 'Phone number is too long (32 characters max)',
    address_label: 'Address',
    address_placeholder: 'Start typing, then pick from the list',
    address_unpinned: 'Pick an address from the list to save it - typed text alone is not saved.',
    trade_label: 'Main trade',
    multi_trade_label: 'This business offers more than one trade',
    secondary_trade_label: 'Additional trades',
    description_label: 'About the business',
    description_placeholder: 'A few sentences customers will see on your page',
    description_too_long: 'Description is too long (2000 characters max)',
    description_counter: '{count} / 2000',
    // #459 - booking policies. Wording copied verbatim from the app's
    // bizPage.* labels (apps/mobile/src/i18n/locales/en.json) - the same
    // question must not be worded two different ways across surfaces.
    policy_heading: 'Policy & terms',
    policy_not_set: 'Not set yet',
    policy_cancellation_label: 'How far in advance can a customer cancel or reschedule for free?',
    policy_cancellation_required_hint: 'Set a cancellation policy to publish your business',
    policy_cancellation_flexible: 'Flexible - cancel anytime',
    policy_cancellation_1h: 'Up to 1 hour before',
    policy_cancellation_2h: 'Up to 2 hours before',
    policy_cancellation_6h: 'Up to 6 hours before',
    policy_cancellation_12h: 'Up to 12 hours before',
    policy_cancellation_24h: 'Up to 24 hours before',
    policy_cancellation_48h: 'Up to 48 hours before',
    policy_cancellation_72h: 'Up to 72 hours before',
    policy_late_arrival_label:
      'How much of a grace period before a late arrival counts as a no-show?',
    policy_late_arrival_none: 'No grace period - any lateness counts as a no-show',
    policy_late_arrival_5: 'Up to 5 minutes late',
    policy_late_arrival_10: 'Up to 10 minutes late',
    policy_late_arrival_15: 'Up to 15 minutes late',
    policy_late_arrival_20: 'Up to 20 minutes late',
    policy_late_arrival_30: 'Up to 30 minutes late',
    policy_no_show_label: "What happens if a customer doesn't show up without cancelling?",
    policy_no_show_no_penalty: 'No penalty - they can book again anytime',
    policy_no_show_may_restrict: 'Repeated no-shows may restrict future bookings',
    policy_no_show_fee_may_apply: 'A no-show fee may apply',
    policy_min_notice_label: 'How far in advance can a customer book?',
    policy_min_notice_none: 'No minimum - can book anytime',
    policy_min_notice_1h: 'At least 1 hour ahead',
    policy_min_notice_2h: 'At least 2 hours ahead',
    policy_min_notice_6h: 'At least 6 hours ahead',
    policy_min_notice_24h: 'At least 24 hours ahead',
    policy_notes_label: 'Additional notes',
    policy_notes_placeholder:
      'E.g. cancellations require 24h notice, arriving 15+ min late may forfeit the slot',
    policy_notes_too_long: 'Notes are too long (2000 characters max)',
    policy_notes_counter: '{count} / 2000',
  },
  services: {
    title: 'Services',
    new_service: 'New service',
    edit_service: 'Edit service',
    name_label: 'Service name',
    vertical_label: 'Specialty',
    gender_label: 'Gender',
    duration_label: 'Duration (minutes)',
    buffer_before_label: 'Buffer before (minutes)',
    buffer_after_label: 'Buffer after (minutes)',
    price_label: 'Price',
    currency_label: 'Currency',
    status_label: 'Status',
    active_badge: 'Active',
    archived_badge: 'Archived',
    archive: 'Archive',
    unarchive: 'Unarchive',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    success_created: 'Service created successfully',
    success_updated: 'Service updated successfully',
    read_only_notice: 'Read-only - no edit permission',
    name_required: 'Service name is required',
    duration_required: 'Duration must be greater than zero',
    no_services: 'No services yet',
    actions_label: 'Actions',
  },
  serviceCategories: {
    title: 'Categories',
    add: 'New category',
    name_label: 'Category name',
    name_placeholder: 'e.g. Hair, Nails',
    rename: 'Rename',
    delete: 'Delete',
    move_up: 'Move up',
    move_down: 'Move down',
    service_count: 'services',
    has_services: 'This category still has services in it. Move or delete them first.',
    empty: 'No categories yet',
    picker_label: 'Category',
    required: 'Please choose a category',
  },
  vertical: {
    hair: 'Hair',
    nails: 'Nails',
    lashes_brows: 'Lashes & Brows',
    makeup: 'Makeup',
    facial: 'Facials',
    massage: 'Massage',
    spa: 'Spa',
    barber: 'Barbershop',
    hair_removal: 'Hair removal',
    holistic: 'Holistic',
    dental: 'Teeth whitening',
    pet_grooming: 'Pet grooming',
  },
  gender: {
    men: 'Men',
    women: 'Women',
    unisex: 'Unisex',
  },
  staff: {
    title: 'Staff',
    new_staff: 'New staff member',
    edit_staff: 'Edit staff member',
    display_name_label: 'Display name',
    display_name_required: 'Display name is required',
    active_badge: 'Active',
    inactive_badge: 'Inactive',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    success_created: 'Staff member created successfully',
    success_updated: 'Staff member updated successfully',
    services_label: 'Services',
    assign_services: 'Assign services',
    save_services: 'Save services',
    read_only_notice: 'Read-only - no edit permission',
    no_staff: 'No staff yet',
    status_label: 'Status',
    deactivate: 'Deactivate',
    activate: 'Activate',
    // T20b: schedule editor
    manage_schedule: 'Manage schedule',
    working_hours: 'Working hours',
    breaks_title: 'Breaks',
    time_off_title: 'Time off',
    save_hours: 'Save hours',
    save_breaks: 'Save breaks',
    add_row: 'Add row',
    remove: 'Remove',
    add_time_off: 'Add time off',
    remove_time_off: 'Remove time off',
    starts_at_label: 'From (date & time)',
    ends_at_label: 'To (date & time)',
    reason_label: 'Reason',
    no_time_off: 'No time off',
    start_time_label: 'Start time',
    end_time_label: 'End time',
    day_of_week_label: 'Day of week',
    day_sun: 'Sunday',
    day_mon: 'Monday',
    day_tue: 'Tuesday',
    day_wed: 'Wednesday',
    day_thu: 'Thursday',
    day_fri: 'Friday',
    day_sat: 'Saturday',
  },
  clients: {
    title: 'Clients',
    search_placeholder: 'Search by name or phone',
    name_label: 'Name',
    phone_label: 'Phone',
    bookings_label: 'Bookings',
    appointments_chip_label: 'appointments',
    last_visit_label: 'Last visit',
    no_visits_yet: 'No visits yet',
    no_clients: 'No clients yet',
    next_page: 'Next page',
    loading_more: 'Loading...',
    back_to_list: 'Back to clients',
    booking_history: 'Booking history',
    no_bookings: 'No bookings',
    service_label: 'Service',
    staff_label: 'Staff',
    date_label: 'Date',
    status_label: 'Status',
    not_found: 'Client not found',
    unknown: 'Unknown',
    registered_at_label: 'Registered on',
    tags_label: 'Tags',
    add_tag_placeholder: 'Add a tag...',
    add_tag_action: 'Add tag',
    remove_tag_aria: 'Remove tag',
    no_tags: 'No tags yet',
  },
  calendar: {
    title: 'Calendar',
    today: 'Today',
    new_booking: 'New Booking',
    no_appointments: 'No appointments for this day',
    loading: 'Loading...',
    // Appointment card
    status_scheduled: 'Scheduled',
    status_cancelled: 'Cancelled',
    status_completed: 'Completed',
    status_no_show: 'No Show',
    reschedule: 'Reschedule',
    cancel_appt: 'Cancel',
    // New booking form
    booking_form_title: 'New Booking',
    phone_label: 'Client phone',
    phone_placeholder: '05X-XXXXXXX',
    name_label: 'Client name (optional)',
    service_label: 'Service',
    staff_label: 'Staff member',
    datetime_label: 'Date & time',
    submit_booking: 'Create booking',
    submitting: 'Submitting...',
    booking_success: 'Booking created successfully',
    slot_unavailable: 'The requested time is unavailable, please choose another',
    duplicate_service_booking: 'This client already has an appointment for this service that day',
    phone_invalid: 'Invalid phone number',
    // Reschedule form
    reschedule_title: 'Reschedule Appointment',
    reschedule_submit: 'Reschedule',
    reschedule_success: 'Appointment rescheduled successfully',
    // Cancel
    cancel_confirm: 'Cancel this appointment?',
    cancel_confirm_title: 'Confirm Cancellation',
    cancel_confirm_yes: 'Confirm cancel',
    cancel_confirm_no: 'Keep appointment',
    cancel_success: 'Appointment cancelled',
    // Filters
    filter_staff: 'Filter by staff',
    all_staff: 'All staff',
    // Select placeholders
    select_service: 'Select service',
    select_staff: 'Select staff',
    // T30 — overlap confirm (only shown when the business turned this on)
    overlap_conflict_title: '{{minutes}}-minute overlap',
    overlap_conflict_divider: 'Overlaps',
    overlap_book_anyway: 'Book anyway',
    overlap_pick_another: 'Pick another time',
    overlap_confirming: 'Booking...',
    // T31 — hours-override confirm (only shown when the business turned this on)
    hours_override_title: 'Outside working hours',
    hours_override_body: "This time is outside the staff member's working hours.",
    // Day grid — "no staff working these hours" hatch
    staff_off_today: 'Not working today',
  },
  public: {
    // Business page — T22
    not_found: 'Business not found',
    services_title: 'Services',
    no_services: 'No services available',
    all_categories: 'All',
    select_service: 'Service',
    select_service_placeholder: 'Select a service',
    select_staff: 'Staff member',
    select_staff_placeholder: 'Select a staff member',
    select_date: 'Date',
    minutes: 'min',
    available_slots: 'Available slots',
    no_slots: 'No available slots for this day',
    instagram: 'Instagram',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    whatsapp: 'WhatsApp',
    follow_us: 'Follow us',
    about_title: 'About',
    get_directions: 'Get directions',
    location_title: 'Location',
    coming_next: 'coming soon',
    // Booking flow — T23a
    book_slot: 'Book this slot',
    phone_label: 'Phone number',
    phone_placeholder: '05X-XXXXXXX',
    send_code: 'Send code',
    sending: 'Sending...',
    code_label: 'Verification code (6 digits)',
    verify_and_book: 'Verify & Book',
    verifying: 'Verifying...',
    booking: 'Booking...',
    back: 'Back',
    wrong_code: 'Invalid or expired code, please try again',
    rate_limited: 'Too many attempts, please try again later',
    slot_unavailable: 'This slot was just taken - please pick another time',
    duplicate_service_booking: 'You already have an appointment for this service that day',
    slot_in_past: 'That time has already passed - please pick a time in the future',
    booking_confirmed: 'Your booking is confirmed!',
    booking_confirmed_detail: 'Booking details',
    booking_error: 'Booking failed, please try again',
    select_slot_first: 'Please select a time slot before booking',
    service_label: 'Service',
    staff_label: 'Staff',
    status_label: 'Status',
    status_pending: 'Scheduled',
    status_scheduled: 'Scheduled',
    status_cancelled: 'Cancelled',
    status_completed: 'Completed',
    status_no_show: 'No show',
    // Manage booking — T23b
    manage_booking: 'Manage booking',
    reschedule: 'Reschedule',
    reschedule_success: 'Rescheduled successfully',
    cancel_booking: 'Cancel booking',
    cancel_confirm: 'Cancel this booking?',
    cancel_confirm_yes: 'Confirm cancellation',
    cancel_confirm_no: 'Keep booking',
    cancel_success: 'Booking cancelled',
    session_expired: 'Session expired - please re-verify your phone',
    pick_new_slot: 'Pick a new time',
    // Calendar export — T24
    add_to_calendar: 'Add to calendar',
    add_to_calendar_loading: 'Downloading...',
    // T4 redesign — slot picker
    pick_date_time_title: 'Pick a date & time',
    confirm_slot: 'Confirm appointment',
    choose_time: 'Select a time',
    morning_group: 'Morning',
    noon_group: 'Afternoon',
    evening_group: 'Evening',
    // T5 redesign — hero, rating, open status, category
    no_reviews_yet: 'No reviews yet',
    open_until: 'Open · closes at {time}',
    closed_today: 'Closed today',
    category_hair: 'Hair salon',
    category_nails: 'Nails',
    category_lashes_brows: 'Lashes & Brows',
    category_makeup: 'Makeup',
    category_facial: 'Facial',
    category_massage: 'Massage',
    category_spa: 'Spa',
    category_barber: 'Barbershop',
    category_hair_removal: 'Hair removal',
    category_holistic: 'Holistic',
    category_dental: 'Teeth whitening',
    category_pet_grooming: 'Pet grooming',
    // #117 — ONE string for both `suspended` and `closed`. They must read
    // identically to a customer, and this must never hint at why: the reason
    // is console-only free text and may say "not paying".
    not_trading: "This business isn't accepting bookings right now",
  },
  not_found: {
    code_label: 'Error 404',
    title: 'This page does not exist',
    body: 'The link may have changed, or the address was typed incorrectly.',
    home: 'Back to home',
  },
} as const;
